import { Injectable, NgZone } from "@angular/core";
import { Observable } from "rxjs";
import { filter, map } from "rxjs/operators";
import { AuthService } from "../../auth/auth.service";
import { Constants } from "../../constants";
import { SseFrameDecoder } from "./sse-frame-decoder";
import {
    SseEvent,
    SseMessage,
    SseOptions,
    SseStreamError,
    SseUntilOptions,
} from "./sse.types";
import { toSseEvent } from "./sse.utils";

const MAX_BACKOFF_MS = 30000;

/**
 * Server-Sent Events client.
 *
 * Three entry points, in increasing order of convenience:
 *
 * ```ts
 * // 1. Raw frames — you handle the payload yourself.
 * this.sse.stream("/some/endpoint/events")
 *     .subscribe((message) => console.log(message.event, message.data));
 *
 * // 2. JSON payloads, typed.
 * this.sse.streamJson<MyPayload>("/some/endpoint/events")
 *     .subscribe((event) => console.log(event.data?.status));
 *
 * // 3. JSON, and the stream ends itself on its own terminal event.
 * this.sse.streamUntil<MyPayload>("/some/endpoint/events", {
 *     isComplete: (e) => e.data?.status === "DONE",
 *     isFailure:  (e) => e.data?.status === "FAILED",
 * }).subscribe({
 *     next:     (e) => this.render(e.data),
 *     error:    (e) => this.showError(e),      // SseStreamError
 *     complete: ()  => this.finish(),
 * });
 * ```
 *
 * Unsubscribing aborts the request, so `takeUntil(this.destroyed$)` — or an
 * `ngOnDestroy` unsubscribe — is all the cleanup a consumer needs.
 *
 * ---
 *
 * Built on fetch + ReadableStream rather than EventSource: EventSource cannot
 * set request headers, and this app authenticates with an `Authorization`
 * header rather than a cookie. Streaming the response by hand keeps that scheme
 * intact and gives real cancellation through AbortController.
 *
 * Behaviour carried over from EventSource: auto-reconnect with backoff,
 * honouring the server's `retry:` hint and resuming with `Last-Event-ID`.
 *
 * The read loop runs outside the Angular zone; every emission is handed back
 * inside `NgZone.run` so change detection still fires.
 */
@Injectable({
    providedIn: "root",
})
export class SseClientService {
    constructor(
        private readonly zone: NgZone,
        private readonly authService: AuthService
    ) {}

    /**
     * Opens a stream and emits each dispatched event as it came off the wire.
     *
     * Completes when the server closes cleanly and no retries remain; errors
     * once reconnection is exhausted.
     *
     * @param url Relative (base domain is prefixed, as with HttpClient) or absolute.
     */
    stream(url: string, options: SseOptions = {}): Observable<SseMessage> {
        const source = this.connect(url, options);
        const names = options.events;

        if (!names || !names.length) {
            return source;
        }
        return source.pipe(filter((message) => names.indexOf(message.event) !== -1));
    }

    /**
     * As {@link stream}, with each payload parsed as JSON.
     *
     * `data` is null when a frame is not valid JSON — `raw` still holds the
     * original text, so plain-text streams remain usable.
     */
    streamJson<T>(url: string, options: SseOptions = {}): Observable<SseEvent<T>> {
        return this.stream(url, options).pipe(map((message) => toSseEvent<T>(message)));
    }

    /**
     * As {@link streamJson}, but the stream ends on its own terminal event
     * instead of waiting for the server to close.
     *
     * `isComplete` emits the event and then completes. `isFailure` errors with
     * an {@link SseStreamError} carrying the offending event. Either way the
     * connection is closed, so no explicit teardown is needed on the happy path.
     */
    streamUntil<T>(url: string, options: SseUntilOptions<T>): Observable<SseEvent<T>> {
        return new Observable<SseEvent<T>>((observer) => {
            const subscription = this.streamJson<T>(url, options).subscribe({
                next: (event) => {
                    if (options.isFailure && options.isFailure(event)) {
                        observer.error(new SseStreamError<T>(event));
                        return;
                    }

                    observer.next(event);

                    if (options.isComplete(event)) {
                        observer.complete();
                    }
                },
                error: (error) => observer.error(error),
                complete: () => observer.complete(),
            });

            return () => subscription.unsubscribe();
        });
    }

    /** The connection itself: fetch, read, decode, reconnect. */
    private connect(url: string, options: SseOptions): Observable<SseMessage> {
        const maxRetries = options.maxRetries === undefined ? 3 : options.maxRetries;
        const baseDelay = options.retryDelayMs === undefined ? 1000 : options.retryDelayMs;
        const withAuth = options.withAuth !== false;
        const target = url.startsWith("http") ? url : Constants.BASE_DOMAIN + url;

        return new Observable<SseMessage>((observer) => {
            const controller = new AbortController();
            const decoder = new SseFrameDecoder();
            let closed = false;
            let attempt = 0;
            let timer: any = null;

            const scheduleReconnect = (error: any) => {
                if (closed) {
                    return;
                }
                if (attempt >= maxRetries) {
                    this.zone.run(() =>
                        error ? observer.error(error) : observer.complete()
                    );
                    return;
                }
                const backoff =
                    decoder.retryMs !== null
                        ? decoder.retryMs
                        : Math.min(baseDelay * Math.pow(2, attempt), MAX_BACKOFF_MS);
                attempt++;
                decoder.reset();
                timer = setTimeout(() => connect(), backoff);
            };

            const connect = () => {
                if (closed) {
                    return;
                }

                const headers: { [key: string]: string } = {
                    Accept: "text/event-stream",
                };
                if (withAuth) {
                    const token = this.authService.getToken();
                    if (token) {
                        headers.Authorization = `${token}`;
                    }
                }
                if (decoder.lastEventId) {
                    headers["Last-Event-ID"] = decoder.lastEventId;
                }

                // Outside the zone so the long-lived read loop does not keep
                // change detection spinning.
                this.zone.runOutsideAngular(() => {
                    fetch(target, {
                        method: "GET",
                        headers,
                        signal: controller.signal,
                        cache: "no-store",
                    })
                        .then((response) => {
                            if (!response.ok) {
                                throw new Error(
                                    `SSE request failed with status ${response.status}`
                                );
                            }
                            if (!response.body) {
                                throw new Error("SSE response has no readable body");
                            }
                            // A successful connect resets the backoff ladder.
                            attempt = 0;
                            return this.consume(response.body, decoder, (message) =>
                                this.zone.run(() => observer.next(message))
                            );
                        })
                        .then(() => {
                            // Server closed the stream.
                            if (!closed) {
                                scheduleReconnect(null);
                            }
                        })
                        .catch((error) => {
                            if (closed || (error && error.name === "AbortError")) {
                                return;
                            }
                            scheduleReconnect(error);
                        });
                });
            };

            connect();

            return () => {
                closed = true;
                if (timer) {
                    clearTimeout(timer);
                }
                controller.abort();
            };
        });
    }

    /** Reads the body and dispatches complete frames as they arrive. */
    private async consume(
        body: ReadableStream<Uint8Array>,
        decoder: SseFrameDecoder,
        emit: (message: SseMessage) => void
    ): Promise<void> {
        const reader = body.getReader();
        const textDecoder = new TextDecoder("utf-8");

        while (true) {
            const chunk = await reader.read();
            if (chunk.done) {
                break;
            }
            // stream: true keeps multi-byte characters intact across chunks.
            for (const message of decoder.push(
                textDecoder.decode(chunk.value, { stream: true })
            )) {
                emit(message);
            }
        }
    }
}
